import React, { useEffect, useState } from "react";
import HeaderUser from "../../components/HeaderUser";
import { AuthService } from "../../services/AuthService";
import { Campaign, UserService } from "../../services/UserService";
import { normalizePhone } from "../../utils/phone";

const inputClass = "w-full px-2.5 py-[9px] rounded-[10px] border border-[#d1d5db] text-sm focus:outline-none focus:border-[#2563eb] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.15)] transition-all";
const labelClass = "flex flex-col gap-1 font-semibold text-[#111827] text-sm";
const saveBtnStyle: React.CSSProperties = { width: "100%", background: "linear-gradient(120deg, #0b3d91, #1e68ff)", color: "#fff", border: "none", borderRadius: "11px", padding: "12px 16px", fontWeight: 700, cursor: "pointer", boxShadow: "0 10px 26px rgba(14,52,161,0.35)", transition: "transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease" };

const ClientTeam: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState<number[]>([]);
  const [form, setForm] = useState({ username: "", full_name: "", email: "", password: "", phone: "" });
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [role] = useState<string | null>(AuthService.getUserRole());

  useEffect(() => {
    const load = async () => {
      try {
        const user = await UserService.getUserWithCamaigns();
        const available = user.campaigns ?? [];
        setCampaigns(available);
        setSelectedCampaigns(available.map(c => c.id).filter((id): id is number => !!id));
      } catch { setError("Não foi possível carregar suas campanhas."); }
    };
    load();
  }, []);

  const toggleCampaign = (id: number) => setSelectedCampaigns(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null); setMessage(null); setPhoneError(null);
    if (role !== "USER_CLIENT_ADMIN") { setError("Apenas clientes administradores podem adicionar usuários."); return; }
    if (!form.username || !form.full_name || !form.email || !form.password) { setError("Preencha usuário, nome, e-mail e senha."); return; }
    const { normalized, error: validationError } = normalizePhone(form.phone);
    if (validationError) { setPhoneError(validationError); return; }
    setLoading(true);
    try {
      await UserService.createClientUser({ username: form.username, full_name: form.full_name.trim(), email: form.email, password: form.password, phone: normalized ?? undefined, campaign_ids: selectedCampaigns });
      setMessage("Usuário criado e vinculado com sucesso.");
      setForm({ username: "", full_name: "", email: "", password: "", phone: "" });
    } catch { setError("Não foi possível criar o usuário."); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#f7f9fc] text-[#0f172a]">
      <HeaderUser campaigns={campaigns} selectedCampaign={campaigns[0] ?? null} onSelectCampaign={() => { }} />
      <div className="max-w-[1100px] mx-auto px-[18px] pt-7 pb-14 flex flex-col gap-4">
        <div className="flex justify-between items-center gap-3">
          <div>
            <p className="uppercase tracking-[0.08em] font-bold text-[#6b7280] mb-1 text-xs m-0">Equipe</p>
            <h2 className="m-0 text-2xl font-bold text-[#0f172a]">Gerenciar usuários da campanha</h2>
            <p className="mt-1 text-[#4b5563] m-0">Adicione usuários clientes às campanhas que você administra.</p>
          </div>
        </div>

        <form
          className="bg-white border border-[#e5e7eb] rounded-xl p-4 flex flex-col gap-2.5 shadow-[0_10px_22px_rgba(15,23,42,0.06)]"
          onSubmit={handleSubmit}
        >
          <h3 className="m-0 mb-1 text-lg">Novo usuário cliente</h3>
          {[
            { label: "Usuário", key: "username" as const, type: "text" },
            { label: "Nome completo", key: "full_name" as const, type: "text", placeholder: "Nome e sobrenome" },
            { label: "E-mail", key: "email" as const, type: "email" },
            { label: "Senha", key: "password" as const, type: "password" },
          ].map(({ label, key, type, placeholder }) => (
            <label key={key} className={labelClass}>
              {label}
              <input type={type} value={form[key]} onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))} placeholder={placeholder} className={inputClass} />
            </label>
          ))}
          <label className={labelClass}>
            Telefone (WhatsApp)
            <input type="tel" value={form.phone} onChange={e => { setPhoneError(null); setForm(prev => ({ ...prev, phone: e.target.value })); }} placeholder="+55..." className={inputClass} />
            {phoneError && <span className="text-red-600 text-xs">{phoneError}</span>}
          </label>

          <div className="flex flex-col gap-1.5">
            <p className="font-semibold text-sm text-[#111827] m-0">Vincular à(s) campanha(s)</p>
            {campaigns.length === 0 && <span className="text-[#9ca3af] text-sm">Nenhuma campanha disponível.</span>}
            <div className="flex gap-2 flex-wrap">
              {campaigns.map(c => (
                <label key={c.id} className={`inline-flex items-center gap-1.5 px-2.5 py-[7px] rounded-[10px] border cursor-pointer text-sm transition-all ${selectedCampaigns.includes(c.id) ? 'bg-blue-50 border-blue-400 text-blue-700 font-semibold' : 'bg-[#f8fafc] border-[#e5e7eb] text-[#374151] hover:border-blue-300'}`}>
                  <input type="checkbox" checked={selectedCampaigns.includes(c.id)} onChange={() => toggleCampaign(c.id)} className="sr-only" />
                  <span>{c.name}</span>
                </label>
              ))}
            </div>
          </div>

          <button type="submit" style={saveBtnStyle} disabled={loading} className="mt-1 disabled:opacity-65 disabled:cursor-not-allowed hover:not(:disabled):-translate-y-px">
            {loading ? "Salvando..." : "Adicionar usuário"}
          </button>
          {message && <div className="text-[#16a34a] font-semibold text-sm">{message}</div>}
          {error && <div className="text-[#b91c1c] font-semibold text-sm">{error}</div>}
        </form>
      </div>
    </div>
  );
};

export default ClientTeam;
